import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FileSpreadsheet, Wrench, Server, ShieldCheck, ArrowUpRight } from 'lucide-react';
import { Reveal } from '@/components/site/Reveal';
import { SectionLabel } from '@/components/site/SectionLabel';
import { CTABand } from '@/components/site/CTABand';
import { FAQList } from '@/components/site/FAQ';
import { FaqSchema } from '@/components/site/Seo';
import { HERO_IMAGES } from '@/data/heroImages';
import {
  ServiceHero, ProblemsBlock, BuildBlock, ApproachBlock,
  OutcomesBlock, RelatedServices,
} from '@/components/site/ServicePageParts';

gsap.registerPlugin(ScrollTrigger);

const PROBLEMS = [
  { slug: 'spreadsheets', icon: FileSpreadsheet, title: 'Outgrowing spreadsheets', body: 'A 4,000-row Google Sheet that three people fight over isn’t a product—it’s a future incident report. We replace it with something that scales.' },
  { slug: 'clunky-tools', icon: Wrench, title: 'Clunky internal tools', body: 'Tools your team avoids cost twice—once in licence, once in the workarounds. We rebuild around the actual workflow, not the vendor’s.' },
  { slug: 'wont-scale', icon: Server, title: 'Systems that won’t scale', body: 'Apps that worked at 200 users break at 2,000. We architect for the next order of magnitude—queues, caching, read-replicas, the boring stuff.' },
  { slug: 'security', icon: ShieldCheck, title: 'Security and reliability', body: 'RBAC, audit logs, encryption, backups, uptime—we treat these as features, not afterthoughts. SOC 2 / ISO-ready by design.' },
];

const BUILD = [
  { slug: 'saas', tag: 'Multi-tenant · Billing', title: 'SaaS platforms', body: 'Multi-tenant apps with billing, plans, usage limits, admin and analytics—everything you need to charge from day one.', accent: '#6C5CE7' },
  { slug: 'admin', tag: 'Internal · Ops', title: 'Internal tools & admin dashboards', body: 'Replace the dozen spreadsheets and Retool patches with a single, opinionated console your team actually opens by default.', accent: '#4B9FFF' },
  { slug: 'portals', tag: 'B2B · Customer', title: 'Customer portals', body: 'Account, billing, support and self-service ops for B2B customers. Cut your support load in half.', accent: '#6C5CE7' },
  { slug: 'marketplaces', tag: 'Two-sided · Payments', title: 'Marketplaces', body: 'Two-sided platforms with onboarding, listings, search, escrow and payouts. Built for trust + take rate.', accent: '#4B9FFF' },
  { slug: 'realtime', tag: 'Sockets · Streams', title: 'Real-time apps', body: 'Collaborative tools, live dashboards and operations apps with websockets, presence and conflict resolution.', accent: '#4B9FFF' },
  { slug: 'apis', tag: 'Platforms · APIs', title: 'API products & platforms', body: 'Public or partner APIs with auth, rate-limits, docs, SDKs and metering. Your data, as a product.', accent: '#6C5CE7' },
];

const APPROACH = [
  { slug: 'discovery', step: '01', title: 'Discovery & architecture', body: 'Two weeks. Stakeholder interviews, data models, integration map, scaling plan. We leave with a one-page brief and a system diagram.' },
  { slug: 'ux-flows', step: '02', title: 'UX flows', body: 'Primary user journeys, edge cases, empty states, error states. Decisions made in low-fi so we argue about behaviour, not pixels.' },
  { slug: 'mvp', step: '03', title: 'MVP', body: 'A thin slice end-to-end—auth, one core flow, one core screen, observability. Real users on it in week 4–6.' },
  { slug: 'scalable-build', step: '04', title: 'Scalable build', body: 'The rest of the app, on the spine the MVP proved. Background workers, multi-tenant guards, caching, the right data store.' },
  { slug: 'qa', step: '05', title: 'QA', body: 'Unit + integration + a real human in a checklist. Performance budgets and a load test before launch—not after.' },
  { slug: 'deploy', step: '06', title: 'Deploy', body: 'Infra as code, blue/green, monitoring, alerting. The ops layer your platform engineer would have built—if you had one.' },
  { slug: 'iterate', step: '07', title: 'Iterate', body: 'A four-week hyper-care window post-launch, then a quarterly cadence. We stay through the second data point, not just the first.' },
];

const CAPS = [
  'Multi-tenant', 'Row-level security', 'RBAC', 'Audit logs', 'Webhooks',
  'GraphQL', 'REST', 'gRPC', 'WebSockets', 'Queues (SQS/Redis)', 'Workers',
  'Postgres', 'MongoDB', 'Redis', 'ClickHouse', 'S3 / R2', 'Kafka',
  'Auth0', 'Clerk', 'Cognito', 'Stripe', 'Razorpay', 'Sentry', 'PostHog',
];

const FAQ = [
  { q: 'Can you take over an existing codebase?', a: 'Yes, after a one-week audit. We will tell you what we are happy to inherit and what we recommend rewriting—with a rough cost and risk attached to each call.' },
  { q: 'What is your stance on no-code platforms?', a: 'They are great until they aren’t. We are happy to start on Retool / Bubble / Glide for v0 and migrate critical pieces to code as the product earns it.' },
  { q: 'Do you handle DevOps and infra?', a: 'Yes. We deploy to Vercel, Fly.io, Render, or your own AWS / GCP. We set up IaC (Terraform / SST), monitoring (Sentry, Grafana) and CI/CD as part of the build.' },
  { q: 'How do you keep our data safe?', a: 'Encrypted at rest and in transit by default. RBAC + audit trails on day one. SOC 2 / ISO-ready architecture. We sign mutual NDAs before the discovery call if needed.' },
  { q: 'What’s a typical timeline?', a: 'A focused v1 (MVP → public release) runs 10–16 weeks. A full platform with billing and multi-tenant ops runs 16–24 weeks.' },
  { q: 'Can you join an existing engineering team?', a: 'Yes. Our Partner engagement model embeds a small NioDelta cell inside your team—standups, retros, the works.' },
];

const OUTCOMES = [
  { kpi: 99.97, suffix: '%', label: 'uptime SLA achieved' },
  { kpi: 8, suffix: 'k', label: 'concurrent users / build' },
  { kpi: 62, prefix: '−', suffix: '%', label: 'support tickets after launch' },
  { kpi: 16, suffix: ' wks', label: 'avg. v1 to production' },
];

const ENGAGEMENT = [
  { title: 'MVP build', length: '6–10 weeks', price: '₹8–18L', body: 'One clear flow, end-to-end. Auth, core actions, lightweight analytics. The thinnest slice that earns the next investment.' },
  { title: 'Dedicated team', length: '3–6 months', price: '₹6L+ / mo', body: 'A fractional NioDelta cell—designer, two engineers, a producer—inside your roadmap.' },
  { title: 'Retainer', length: '6 months+', price: '₹3L+ / mo', body: 'Post-launch support, growth features, performance and security work on a steady cadence.' },
];

/* ----- Signature: Interactive dashboard that animates on scroll ----- */
function DashboardMockup() {
  const root = useRef(null);
  const numsRef = useRef({ revenue: 0, mrr: 0, churn: 0, signups: 0 });
  const [nums, setNums] = useState({ revenue: 0, mrr: 0, churn: 0, signups: 0 });
  const barsRef = useRef(null);
  const lineRef = useRef(null);

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !root.current) return;
    const ctx = gsap.context(() => {
      const targets = { revenue: 184230, mrr: 24800, churn: 2.4, signups: 612 };
      gsap.to(numsRef.current, {
        revenue: targets.revenue, mrr: targets.mrr, churn: targets.churn, signups: targets.signups,
        ease: 'power2.out',
        scrollTrigger: { trigger: root.current, start: 'top 75%', end: 'bottom 30%', scrub: 0.6 },
        onUpdate: () => setNums({ ...numsRef.current }),
      });
      // bars
      const bars = barsRef.current ? barsRef.current.querySelectorAll('[data-bar]') : [];
      bars.forEach((b, i) => {
        const h = parseFloat(b.dataset.target);
        gsap.fromTo(b, { height: '4%' }, {
          height: `${h}%`,
          ease: 'power2.out',
          scrollTrigger: { trigger: root.current, start: 'top 75%', end: 'bottom 40%', scrub: 0.6 },
          delay: i * 0.03,
        });
      });
      // line stroke
      if (lineRef.current) {
        const path = lineRef.current;
        const len = path.getTotalLength();
        path.style.strokeDasharray = String(len);
        path.style.strokeDashoffset = String(len);
        gsap.to(path, {
          strokeDashoffset: 0,
          ease: 'none',
          scrollTrigger: { trigger: root.current, start: 'top 75%', end: 'bottom 40%', scrub: 0.6 },
        });
      }
    }, root);
    return () => ctx.revert();
  }, []);

  const bars = [22, 38, 30, 55, 47, 62, 70, 58, 76, 82, 70, 90];
  const inr = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`;

  return (
    <div
      ref={root}
      data-testid="wa-dashboard-mockup"
      className="relative aspect-[16/10] w-full rounded-[20px] overflow-hidden border border-white/[0.12] bg-[#0E0E15]"
    >
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
      <div
        className="absolute -right-32 -top-32 h-[420px] w-[420px] rounded-full blur-[120px] pointer-events-none"
        style={{ background: 'rgba(108,92,231,0.30)' }}
      />
      {/* top bar */}
      <div className="relative h-12 border-b border-white/[0.08] px-5 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[12px] font-semibold tracking-tight">
          <svg viewBox="0 0 32 32" className="h-4 w-4"><path d="M16 6 L26 24 L6 24 Z" fill="none" stroke="#6C5CE7" strokeWidth="2"/></svg>
          Kaveri<span className="text-white/50">Console</span>
        </div>
        <div className="flex gap-3 text-[10px] uppercase tracking-[0.22em] text-white/40">
          <span>Overview</span><span className="text-white">Revenue</span><span>Customers</span><span>Settings</span>
        </div>
      </div>
      <div className="p-5 md:p-7 grid grid-cols-12 gap-4 h-[calc(100%-3rem)]">
        {/* KPI tiles */}
        <div className="col-span-12 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { l: 'Revenue · MTD', v: inr(nums.revenue), c: '#6C5CE7' },
            { l: 'MRR', v: inr(nums.mrr), c: '#4B9FFF' },
            { l: 'Churn (rolling 30d)', v: `${nums.churn.toFixed(1)}%`, c: '#6C5CE7' },
            { l: 'Signups · 7d', v: Math.round(nums.signups).toString(), c: '#4B9FFF' },
          ].map((k) => (
            <div key={k.l} className="rounded-xl border border-white/[0.12] bg-white/[0.02] p-4">
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/50">{k.l}</div>
              <div className="mt-2 text-xl md:text-2xl font-bold tracking-tight tabular-nums" style={{ color: k.c }}>
                {k.v}
              </div>
            </div>
          ))}
        </div>
        {/* Bars */}
        <div ref={barsRef} className="col-span-12 md:col-span-7 rounded-xl border border-white/[0.12] bg-white/[0.02] p-4 flex flex-col">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/50">Revenue · last 12 weeks</div>
            <div className="text-[10px] text-white/40">All amounts ₹</div>
          </div>
          <div className="mt-3 flex-1 flex items-end gap-1.5">
            {bars.map((h, i) => (
              <div key={i} className="flex-1 rounded-sm" data-bar data-target={h}
                style={{
                  background: i === bars.length - 1 ? '#6C5CE7' : 'rgba(108,92,231,0.35)',
                  height: '4%',
                }}
              />
            ))}
          </div>
        </div>
        {/* Line chart */}
        <div className="col-span-12 md:col-span-5 rounded-xl border border-white/[0.12] bg-white/[0.02] p-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/50">Active sessions · today</div>
          <svg viewBox="0 0 200 80" className="mt-3 w-full h-[120px]">
            <defs>
              <linearGradient id="lineG" x1="0" x2="1">
                <stop offset="0%" stopColor="#6C5CE7" />
                <stop offset="100%" stopColor="#4B9FFF" />
              </linearGradient>
            </defs>
            <path
              ref={lineRef}
              d="M0,60 C20,55 30,40 45,38 C65,36 75,55 95,50 C115,46 130,18 150,22 C170,26 185,12 200,8"
              fill="none"
              stroke="url(#lineG)"
              strokeWidth="2"
            />
            {/* dots */}
            {[{x:45,y:38},{x:95,y:50},{x:150,y:22},{x:200,y:8}].map((d, i) => (
              <circle key={i} cx={d.x} cy={d.y} r="2.5" fill="#fff" />
            ))}
          </svg>
        </div>
      </div>
    </div>
  );
}

export default function WebAppDevelopment() {
  const techRef = useRef(null);

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !techRef.current) return;
    const ctx = gsap.context(() => {
      gsap.to('.wa-marquee-inner', { xPercent: -50, ease: 'none', duration: 32, repeat: -1 });
    }, techRef);
    return () => ctx.revert();
  }, []);

  return (
    <div data-testid="web-app-development-page">
      <FaqSchema items={FAQ} />
      <ServiceHero
        image={HERO_IMAGES['web-app-development']}
        serviceNum="02"
        serviceTitle="Web App Development"
        headlineText="Web applications that handle real work."
        accent="work"
        description="Multi-tenant SaaS, customer portals, marketplaces and dashboards engineered for the order of magnitude after the one you’re at. Boring infra, fast UI, observability you can sleep on."
        secondaryCta={{ label: 'See app work', to: '/work/' }}
        testIdPrefix="wa"
      />

      <ProblemsBlock
        heading="Four signs your app stack is the bottleneck."
        accentWord="bottleneck."
        intro="You don’t need a rewrite—you need someone to find the three things actually holding you back, fix them, and leave the rest alone."
        items={PROBLEMS}
        testIdPrefix="wa"
      />

      <BuildBlock
        heading="Six shapes of web app."
        accentWord="web app."
        intro="Every app is its own thing—but most live in one of these six shapes. We pick the right model in week one so the build matches the business."
        items={BUILD}
        testIdPrefix="wa"
      />

      <ApproachBlock
        heading="Seven stages. One file."
        accentWord="One file."
        intro="Designers and engineers in the same file from day one. Architecture before screens. Observability before launch."
        steps={APPROACH}
        testIdPrefix="wa"
      />

      {/* Capabilities marquee + emphasis row */}
      <section
        ref={techRef}
        data-testid="wa-capabilities"
        className="relative py-24 md:py-28 border-y border-white/10 bg-ink-surface/30 overflow-hidden"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 mb-12 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <SectionLabel num="05">Capabilities</SectionLabel>
            <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05] max-w-2xl text-balance">
              A toolbox we’d <span className="italic text-electric">defend</span> in a system review.
            </h2>
          </div>
          <div className="md:col-span-4 md:col-start-9">
            <Reveal>
              <p className="text-ink-muted leading-relaxed max-w-md">
                We aren’t precious about tools—we are precious about what they do in production. Each pick earns its place.
              </p>
            </Reveal>
          </div>
        </div>
        <div className="relative">
          <div className="absolute left-0 top-0 bottom-0 w-32 z-10 bg-gradient-to-r from-ink-bg to-transparent pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-32 z-10 bg-gradient-to-l from-ink-bg to-transparent pointer-events-none" />
          <div className="flex whitespace-nowrap wa-marquee-inner gap-6 will-change-transform">
            {[...CAPS, ...CAPS].map((t, i) => (
              <div key={i} className="shrink-0 inline-flex items-center gap-3 px-6 py-3.5 rounded-full border border-white/[0.12] bg-ink-bg text-sm tracking-tight">
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: i % 2 === 0 ? '#6C5CE7' : '#4B9FFF' }} />
                {t}
              </div>
            ))}
          </div>
        </div>
        {/* Emphasis row */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 mt-16 grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { k: 'Scalability', v: 'Designed for 10× your current load on day one. Read-replicas, queues and caching baked in.' },
            { k: 'Security', v: 'RBAC, audit logs, encryption, secrets, SAST in CI. SOC 2 / ISO-friendly architecture.' },
            { k: 'Integrations', v: 'Payments, CRMs, email/SMS, identity, accounting—the boring connectors that make a product useful.' },
          ].map((e, i) => (
            <Reveal key={e.k} delay={i * 90}>
              <div className="rounded-[16px] border border-white/[0.12] bg-ink-bg p-6 md:p-7 h-full">
                <div className="text-[11px] uppercase tracking-[0.28em] text-ink-muted">— Pillar 0{i + 1}</div>
                <div className="mt-4 text-xl font-semibold tracking-tight">{e.k}</div>
                <p className="mt-3 text-ink-muted leading-relaxed text-sm">{e.v}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Signature — interactive dashboard mockup */}
      <section data-testid="wa-signature" className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14 items-end">
            <div className="md:col-span-7">
              <SectionLabel num="06">Illustration · Live dashboard</SectionLabel>
              <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05] max-w-3xl text-balance">
                Demo console — <span className="italic text-violet">scroll the page,</span> watch the numbers move.
              </h2>
            </div>
            <div className="md:col-span-5 md:pl-6 md:border-l border-white/10">
              <Reveal>
                <p className="text-ink-muted leading-relaxed max-w-md">
                  An illustrative operations console for a lender, not a client build: loan-book metrics, a disbursal heatmap and live collections on one screen.
                </p>
              </Reveal>
              
            </div>
          </div>
          <Reveal>
            <DashboardMockup />
          </Reveal>
        </div>
      </section>

      <OutcomesBlock
        heading="Numbers we’d sign a contract against."
        accentWord="contract"
        intro="Medians across the last 12 web-app engagements. Individual results vary—measurement doesn’t."
        values={OUTCOMES}
        testIdPrefix="wa"
      />

      {/* Engagement models */}
      <section data-testid="wa-engagements" className="relative py-24 md:py-32 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-5">
            <SectionLabel num="08">Engagement models</SectionLabel>
            <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight leading-[1.1]">
              Three ways to <span className="italic text-violet">work with us.</span>
            </h2>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {ENGAGEMENT.map((p, i) => (
            <Reveal key={p.title} delay={i * 90}>
              <div data-testid={`wa-engagement-${p.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="group h-full rounded-[20px] border border-white/[0.12] bg-ink-surface p-7 transition-all duration-500 hover:-translate-y-1.5 hover:border-white/25 hover:shadow-[0_20px_60px_-30px_rgba(108,92,231,0.45)]">
                <div className="text-[11px] uppercase tracking-[0.28em] text-ink-muted">{p.length}</div>
                <div className="mt-4 text-3xl font-bold tracking-tight">{p.title}</div>
                <div className="mt-1 text-violet font-medium tabular-nums">{p.price}</div>
                <p className="mt-5 text-sm text-ink-muted leading-relaxed">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="wa-faq" className="relative py-24 md:py-32 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <SectionLabel num="09">FAQ</SectionLabel>
            <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight leading-[1.1]">
              Things we’re asked <span className="italic text-violet">before</span> the call.
            </h2>
          </div>
          <div className="md:col-span-8">
            <FAQList items={FAQ} testIdPrefix="wa-faq" />
          </div>
        </div>
      </section>

      <RelatedServices exclude="web-app-development" testIdPrefix="wa" />

      <CTABand
        eyebrow="Ready for the next order of magnitude?"
        headline="Tell us what your app needs to do."
        sub="A 30-minute WhatsApp call with a partner. Bring the screenshot of the dashboard you wish you had."
      />
    </div>
  );
}

// Need ArrowUpRight import? It's used in MagneticButton via ServiceHero already.
// keeping unused import line removed:
void ArrowUpRight;
